import { useState } from 'react';

import Button from 'components/ui/button';
import { ExperienceCatalogQuestions } from 'types/experienceCatalog';
import { FOCO_VISIVEL } from 'utils/foco';

export interface RespostaDaAvaliacao {
  notas: Record<number, number>;
  comentario: string;
  identificar: boolean;
}

interface AvaliacaoDoCatalogoProps {
  perguntas?: ExperienceCatalogQuestions[];
  jaAvaliou: boolean;
  /** Rejeita quando o envio falha: o formulário mostra o erro e libera um novo envio. */
  onEnviar: (resposta: RespostaDaAvaliacao) => Promise<void>;
}

const ESTRELA = 'm12 2 2.9 6.3 6.9.8-5 4.8 1.3 6.8L12 17.5 5.9 20.7 7.2 13.9l-5-4.8 6.9-.8z';

// Marcada e vazia se distinguem pelo traço, não só pela cor: em cinza e na lupa, o contorno grosso e escuro
// fica contra o fino e claro. No alto contraste do Windows, cores do sistema.
const TRACO_DA_ESTRELA = {
  marcada: {
    classes:
      'fill-brand-yellow stroke-brand-blue-dark [@media(forced-colors:active)]:fill-[Highlight] [@media(forced-colors:active)]:stroke-[color:Highlight]',
    espessura: '2.6',
  },
  vazia: {
    classes: 'fill-none stroke-content-soft [@media(forced-colors:active)]:stroke-[color:CanvasText]',
    espessura: '1.2',
  },
};

/** Nota de 1 a 5 como rádios nativos: as setas trocam a nota e o grupo é nomeado pela pergunta. */
function NotaPorEstrelas({ pergunta, indice, nota, onNota }: { pergunta: string; indice: number; nota: number; onNota: (nota: number) => void }) {
  return (
    <fieldset className="m-0 border-0 border-b border-solid border-surface-200 p-0 py-4 last:border-b-0">
      <legend className="float-left mb-2 w-full p-0 text-[14px] leading-[1.5] text-content">
        <span className="font-bold text-brand-blue-dark">Pergunta {indice + 1}:</span> {pergunta}
      </legend>
      <div className="clear-both flex items-center gap-1">
        {[1, 2, 3, 4, 5].map(valor => {
          const traco = nota >= valor ? TRACO_DA_ESTRELA.marcada : TRACO_DA_ESTRELA.vazia;
          return (
            // O rádio cobre a estrela inteira, transparente: é ele que recebe o clique, o foco e o nome.
            <span key={valor} className="relative block">
              <input
                type="radio"
                name={`pergunta-${indice}`}
                value={valor}
                checked={nota === valor}
                onChange={() => onNota(valor)}
                aria-label={valor === 1 ? '1 estrela' : `${valor} estrelas`}
                className="peer absolute inset-0 m-0 h-full w-full cursor-pointer opacity-0"
              />
              <svg
                width="30"
                height="30"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
                className={`block rounded peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-blue-dark peer-focus-visible:[@media(forced-colors:active)]:outline-[color:Highlight] ${traco.classes}`}
                strokeWidth={traco.espessura}
                strokeLinejoin="round"
              >
                <path d={ESTRELA} />
              </svg>
            </span>
          );
        })}
        {/* A nota escrita serve à lupa e a quem não distingue as cores; o leitor de tela já a ouve no rádio marcado. */}
        {nota > 0 && (
          <span aria-hidden="true" className="ml-2 text-[14px] font-bold text-brand-blue-dark">
            {nota} de 5
          </span>
        )}
      </div>
    </fieldset>
  );
}

/** "Queremos te ouvir!": uma nota por pergunta, comentário, identificação e envio (CAT-35). */
export default function AvaliacaoDoCatalogo({ perguntas, jaAvaliou, onEnviar }: AvaliacaoDoCatalogoProps) {
  const [notas, setNotas] = useState<Record<number, number>>({});
  const [comentario, setComentario] = useState('');
  const [identificar, setIdentificar] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [erroNoEnvio, setErroNoEnvio] = useState(false);

  if (jaAvaliou) {
    return (
      <section role="status" aria-labelledby="titulo-avaliacao-enviada" className="mb-6 rounded-xl bg-brand-white px-6 py-7 shadow-jj-01 sm:px-8">
        <h2 id="titulo-avaliacao-enviada" tabIndex={0} className={`m-0 mb-2 font-display text-[22px] font-extrabold text-brand-blue-dark ${FOCO_VISIVEL}`}>
          Agradecemos por compartilhar sua opinião!
        </h2>
        <p className="m-0 text-[15px] leading-[1.6] text-content">
          Seu feedback é muito importante para continuarmos melhorando nossas experiências.
        </p>
      </section>
    );
  }

  const total = perguntas?.length ?? 0;
  const respondidas = Object.keys(notas).length;
  // Sem pergunta cadastrada não há o que avaliar: o envio fica fechado.
  const completa = total > 0 && respondidas >= total;
  const rotuloDoEnvio = !perguntas ? 'Carregando...' : enviando ? 'Enviando...' : completa ? 'Enviar Avaliação' : `Enviar Avaliação (${respondidas}/${total})`;

  const enviar = async () => {
    setEnviando(true);
    setErroNoEnvio(false);
    try {
      await onEnviar({ notas, comentario, identificar });
    } catch (erro) {
      console.error('Erro ao enviar a avaliação do catálogo:', erro);
      setErroNoEnvio(true);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <section aria-labelledby="titulo-avaliacao" className="mb-6 rounded-xl bg-brand-white px-6 py-7 shadow-jj-01 sm:px-8">
      <h2 id="titulo-avaliacao" tabIndex={0} className={`m-0 mb-2 font-display text-[22px] font-extrabold text-brand-blue-dark ${FOCO_VISIVEL}`}>
        Queremos te ouvir!
      </h2>
      <p tabIndex={0} className={`m-0 mb-4 text-[15px] leading-[1.6] text-content ${FOCO_VISIVEL}`}>
        Apenas algumas perguntas sobre o Catálogo de Experiências para que possamos aprimorar nossas ações de reconhecimento e
        premiação. Vamos lá?
      </p>

      {perguntas?.map((item, indice) => (
        <NotaPorEstrelas
          key={item._id ?? indice}
          pergunta={item.pergunta}
          indice={indice}
          nota={notas[indice] ?? 0}
          onNota={nota => setNotas(anteriores => ({ ...anteriores, [indice]: nota }))}
        />
      ))}

      <label htmlFor="comentario-avaliacao" className="mt-5 block text-[14px] font-bold text-brand-blue-dark">
        Comentários e sugestões (opcional):
      </label>
      <textarea
        id="comentario-avaliacao"
        value={comentario}
        onChange={evento => setComentario(evento.target.value)}
        placeholder="O que você mais gostou nesse modelo; o que pode melhorar; etc..."
        aria-describedby="comentario-avaliacao-dica"
        rows={4}
        className={`mt-2 box-border w-full resize-y rounded-md border border-solid border-content-soft px-3 py-2.5 font-text text-[14px] text-content ${FOCO_VISIVEL}`}
      />
      <p id="comentario-avaliacao-dica" className="m-0 mt-1 text-[12px] text-content-soft">
        Compartilhe suas impressões sobre a experiência escolhida
      </p>

      <fieldset className="m-0 mt-5 border-0 p-0">
        <legend className="mb-2 p-0 text-[14px] font-bold text-brand-blue-dark">Deseja se identificar:</legend>
        <div className="flex gap-6">
          {[
            { valor: false, rotulo: 'Não' },
            { valor: true, rotulo: 'Sim' },
          ].map(opcao => (
            <label key={opcao.rotulo} className="flex cursor-pointer items-center gap-2 text-[14px] text-content">
              <input
                type="radio"
                name="identificar-avaliacao"
                checked={identificar === opcao.valor}
                onChange={() => setIdentificar(opcao.valor)}
                className={`h-[18px] w-[18px] cursor-pointer accent-brand-blue ${FOCO_VISIVEL}`}
              />
              {opcao.rotulo}
            </label>
          ))}
        </div>
      </fieldset>

      <p role="note" className="m-0 mt-5 rounded-lg bg-brand-yellow-light px-4 py-3 text-[12px] leading-[1.6] text-content">
        Essa avaliação de escolha de experiências faz parte de um teste e portanto o resultado final será utilizado somente para
        avaliação dessa nova metodologia.
      </p>

      <div className="mt-6">
        {erroNoEnvio && (
          <p role="alert" className="m-0 mb-3 rounded-lg bg-surface-100 px-4 py-3 text-[13px] font-bold leading-[1.5] text-[#B3123A]">
            Não foi possível enviar a sua avaliação. Tente novamente.
          </p>
        )}
        <Button
          variant="solido"
          disabled={!completa || enviando}
          onClick={enviar}
          aria-describedby={perguntas && !completa ? 'avaliacao-pendente' : undefined}
        >
          {rotuloDoEnvio}
        </Button>
        {total > 0 && !completa && (
          <p id="avaliacao-pendente" className="sr-only">
            Por favor, responda todas as {total} perguntas para enviar sua avaliação
          </p>
        )}
      </div>
    </section>
  );
}
